import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import notificationService from '../services/notificationService';
import apiService from '../services/api';
import { getNotificationCategory, normalizeNotificationType } from '../utils/notifications';

// Notification types
export const NOTIFICATION_TYPES = {
  NEW_PROPOSAL: 'New Proposal',
  NEW_QUOTATION: 'New Quotation',
  NEW_NEGOTIATION: 'New Negotiation',
  JOB_COMPLETED: 'job_completed',
  SYSTEM_MESSAGE: 'system_message'
};

// Notification categories
export const NOTIFICATION_CATEGORIES = {
  ALL: 'all',
  PROPOSALS: 'proposals',
  QUOTATIONS: 'quotations',
  NEGOTIATIONS: 'negotiations',
  JOBS: 'jobs',
  SYSTEM: 'system'
};

// Initial state
const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  connectionStatus: {
    isConnected: false,
    connectionId: null,
    state: 'Disconnected',
    isWebSocketDisabled: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 5
  }
};

// Action types (READ-ONLY)
const NOTIFICATION_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  CLEAR_ERROR: 'CLEAR_ERROR',
  MARK_ALL_AS_READ: 'MARK_ALL_AS_READ'
};

// Reducer
const notificationReducer = (state, action) => {
  switch (action.type) {
    case NOTIFICATION_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case NOTIFICATION_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case NOTIFICATION_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case NOTIFICATION_ACTIONS.SET_NOTIFICATIONS: {
      const notifications = action.payload;
      const unreadCount = (notifications || []).filter((n) => !n.read).length;
      return {
        ...state,
        notifications,
        unreadCount,
        isLoading: false,
        error: null
      };
    }

    case NOTIFICATION_ACTIONS.ADD_NOTIFICATION: {
      const newNotification = {
        ...action.payload,
        id: action.payload.id || Date.now(),
        timestamp: action.payload.timestamp || new Date().toISOString(),
        read: false
      };

      // Check if notification already exists to prevent duplicates
      const existingNotification = state.notifications.find(
        (n) =>
          n.id === newNotification.id ||
          (n.type === newNotification.type &&
            n.message === newNotification.message &&
            Math.abs(new Date(n.timestamp) - new Date(newNotification.timestamp)) < 5000) // Within 5 seconds
      );

      if (existingNotification) {
        // Update existing notification instead of adding duplicate
        const updatedNotifications = state.notifications.map((notification) =>
          notification.id === existingNotification.id
            ? { ...notification, ...newNotification, read: false }
            : notification
        );

        const newUnreadCount = updatedNotifications.filter((n) => !n.read).length;
        return {
          ...state,
          notifications: updatedNotifications,
          unreadCount: newUnreadCount
        };
      }

      const updatedNotifications = [newNotification, ...state.notifications];
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: state.unreadCount + 1
      };
    }

    case NOTIFICATION_ACTIONS.MARK_ALL_AS_READ:
      return {
        ...state,
        notifications: (state.notifications || []).map((notification) => ({
          ...notification,
          read: true
        })),
        unreadCount: 0
      };

    case NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS:
      return {
        ...state,
        connectionStatus: action.payload
      };

    default:
      return state;
  }
};

// Context
const NotificationContext = createContext(null);

// Provider component
export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  // Load notifications from API
  const loadNotifications = useCallback(async () => {
    try {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_LOADING, payload: true });

      const response = await apiService.get('/Notifications', {
        requireAuth: true
      });

      // Handle different response structures
      let notificationsData = [];

      if (response.data) {
        // Check if response.data is an array or has a data property
        notificationsData = Array.isArray(response.data) ? response.data : response.data.data || [];
      } else if (Array.isArray(response)) {
        notificationsData = response;
      }

      // Ensure notifications have proper structure and remove duplicates
      const notifications = (notificationsData || []).map((notification) => {
        // Normalize notification type (handles both numeric and string types)
        let notificationType = normalizeNotificationType(notification.type);

        // If normalization didn't work and we have a message, try content-based detection
        if (notificationType === 'system_message' && notification.message) {
          const message = notification.message.toLowerCase();

          // Precise matching to avoid cross-contamination
          if (message.includes('quotation') && message.includes('awaiting your approval')) {
            notificationType = 'New Quotation';
          } else if (message.includes('proposal') && message.includes('awaiting your response')) {
            notificationType = 'New Proposal';
          } else if (message.includes('negotiation') || message.includes('negotiate')) {
            notificationType = 'New Negotiation';
          } else if (message.includes('job') && message.includes('completed')) {
            notificationType = 'job_completed';
          }
        }

        const processedNotification = {
          ...notification,
          id: notification.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
          read: Boolean(notification.read ?? notification.isRead),
          type: notificationType,
          category: getNotificationCategory(notificationType) || 'system'
        };

        return processedNotification;
      });

      // Remove duplicates based on ID only (simpler and more reliable)
      const uniqueNotifications = (notifications || []).filter(
        (notification, index, self) => index === self.findIndex((n) => n.id === notification.id)
      );


      dispatch({ type: NOTIFICATION_ACTIONS.SET_NOTIFICATIONS, payload: uniqueNotifications });
    } catch (error) {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: NOTIFICATION_ACTIONS.CLEAR_ERROR });
  }, []);

  // Get filtered notifications by category
  const getNotificationsByCategory = useCallback(
    (category) => {
      if (category === 'all') {
        return state.notifications;
      }
      return (state.notifications || []).filter((notification) => notification.category === category);
    },
    [state.notifications]
  );

  // Get notifications by type
  const getNotificationsByType = useCallback(
    (type) => {
      return (state.notifications || []).filter((notification) => notification.type === type);
    },
    [state.notifications]
  );

  // Get notifications by the three major types
  const getProposals = useCallback(() => {
    return (state.notifications || []).filter((notification) => notification.type === 'New Proposal');
  }, [state.notifications]);

  const getQuotations = useCallback(() => {
    return (state.notifications || []).filter((notification) => notification.type === 'New Quotation');
  }, [state.notifications]);

  const getNegotiations = useCallback(() => {
    return (state.notifications || []).filter((notification) => notification.type === 'New Negotiation');
  }, [state.notifications]);

  // Initialize SignalR connection for real-time notifications
  const initializeConnection = useCallback(async () => {
    try {
      // Request notification permission for browser notifications
      await notificationService.requestNotificationPermission();

      // Set up event listeners for real-time updates
      notificationService.on('notification', (notification) => {
        // Process the notification with the same logic as loadNotifications
        const processedNotification = {
          ...notification,
          id: notification.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: notification.timestamp || notification.createdAt || new Date().toISOString(),
          read: Boolean(notification.read ?? notification.isRead),
          type: notification.type || 'system_message',
          category: getNotificationCategory(notification.type) || 'system'
        };

        dispatch({ type: NOTIFICATION_ACTIONS.ADD_NOTIFICATION, payload: processedNotification });
      });

      // SignalR connection event handlers
      notificationService.on('connectionClosed', () => {
        dispatch({
          type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS,
          payload: { isConnected: false, state: 'Disconnected' }
        });
      });

      notificationService.on('reconnecting', ({ attempt }) => {
        dispatch({
          type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS,
          payload: {
            isConnected: false,
            state: 'Reconnecting',
            reconnectAttempts: attempt
          }
        });
      });

      notificationService.on('reconnected', () => {
        const status = notificationService.getConnectionStatus();
        dispatch({ type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS, payload: status });
      });

      // Initialize SignalR connection
      const connected = await notificationService.connect();

      if (connected) {
        // Update connection status
        const status = notificationService.getConnectionStatus();
        dispatch({ type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS, payload: status });
      }
    } catch (error) {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  // Disconnect SignalR connection
  const disconnect = useCallback(async () => {
    try {
      await notificationService.disconnect();
      dispatch({
        type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS,
        payload: { isConnected: false, state: 'Disconnected' }
      });
    } catch {
      return;
    }
  }, []);

  // Retry SignalR connection
  const retryConnection = useCallback(async () => {
    try {
      // Try to connect again
      await notificationService.connect();

      // Update connection status
      const status = notificationService.getConnectionStatus();
      dispatch({ type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS, payload: status });
    } catch (error) {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {

      await notificationService.markAllAsRead();

      dispatch({ type: NOTIFICATION_ACTIONS.MARK_ALL_AS_READ });

    } catch (error) {
      dispatch({ type: NOTIFICATION_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, []);

  // Initialize on mount - load notifications once, then use SignalR for updates
  useEffect(() => {
    // Check if user is authenticated
    const authData = JSON.parse(localStorage.getItem('auth') || '{}');
    if (authData?.token) {
      // Load notifications once on initialization
      loadNotifications();
      // Initialize SignalR for real-time updates
      initializeConnection();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [loadNotifications, initializeConnection, disconnect]);

  // Update connection status periodically (reduced frequency)
  useEffect(() => {
    const interval = setInterval(() => {
      const status = notificationService.getConnectionStatus();
      dispatch({ type: NOTIFICATION_ACTIONS.SET_CONNECTION_STATUS, payload: status });
    }, 30000); // Check every 30 seconds instead of 5 seconds

    return () => clearInterval(interval);
  }, []);

  const value = {
    // State
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    isLoading: state.isLoading,
    error: state.error,
    connectionStatus: state.connectionStatus,

    // Actions (READ-ONLY)
    loadNotifications,
    clearError,
    markAllAsRead,

    // Utilities
    getNotificationsByCategory,
    getNotificationsByType,
    getProposals,
    getQuotations,
    getNegotiations,

    // Connection management
    initializeConnection,
    disconnect,
    retryConnection
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

// Hook to use notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
